import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5QrcodeScanner } from "html5-qrcode";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db, auth } from "./firebase";

const allowedEmails = ["dipeshbalodiya3@gmail.com", "umangsharma122005@gmail.com", "pd240706@gmail.com"];

const VolunteerPage = () => {
  const [scanResult, setScanResult] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [error, setError] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [memberAttendance, setMemberAttendance] = useState({});
  const [leaderAttendance, setLeaderAttendance] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user || !allowedEmails.includes(user.email)) {
        navigate("/");
      } else {
        setUserEmail(user.email);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (userEmail) {
      const scanner = new Html5QrcodeScanner("qr-reader", {
        fps: 10,
        qrbox: 250,
      });

      scanner.render(
        async (decodedText) => {
          setScanResult(decodedText);
          setError(null);
          await fetchTeamData(decodedText);
        },
        () => {
          setError("QR Code not detected. Try again.");
        }
      );

      return () => scanner.clear();
    }
  }, [userEmail]);

  const fetchTeamData = async (teamId) => {
    try {
      const teamRef = doc(db, "teams", teamId);
      const teamSnap = await getDoc(teamRef);

      if (teamSnap.exists()) {
        const data = teamSnap.data();
        setTeamData(data);
        
        // Set initial attendance states
        setLeaderAttendance(data.teamLeaderAttendance || false);
        const initialAttendance = {};
        (data.teamMembers || []).forEach((member) => {
          initialAttendance[member.name] = member.attendance || false;
        });
        setMemberAttendance(initialAttendance);
      } else {
        setError("Invalid QR Code: Team not found.");
      }
    } catch (err) {
      console.error("Error fetching team data: ", err);
      setError("Error fetching team data. Try again.");
    }
  };

  const toggleMemberAttendance = (memberName) => {
    setMemberAttendance((prev) => ({
      ...prev,
      [memberName]: !prev[memberName],
    }));
  };

  const markAttendance = async () => {
    try {
      const teamRef = doc(db, "teams", scanResult);
      const updatedMembers = teamData.teamMembers.map((member) => ({
        ...member,
        attendance: memberAttendance[member.name],
      }));

      const allPresent = leaderAttendance && Object.values(memberAttendance).every((status) => status);

      await updateDoc(teamRef, {
        teamLeaderAttendance: leaderAttendance,
        teamMembers: updatedMembers,
        attendance: allPresent,
      });

      alert("Attendance updated successfully!");
      setScanResult(null);
      setTeamData(null);
    } catch (err) {
      console.error("Error updating attendance: ", err);
      setError("Error marking attendance. Try again.");
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
      <h2 className="text-2xl font-bold mb-4 text-blue-700">Volunteer QR Scanner</h2>

      {scanResult ? (
        <div className="bg-white p-4 rounded shadow-md w-full max-w-md">
          <p className="text-lg font-semibold text-green-600">Scan Successful!</p>
          <p className="text-gray-700">Team ID: {scanResult}</p>

          {teamData ? (
            <div className="mt-4 p-4 border border-blue-400 rounded-lg">
              <h3 className="text-xl font-bold text-blue-700">{teamData.teamName}</h3>
              <p><strong>Leader:</strong> {teamData.teamLeaderSalutation} {teamData.teamLeaderName}</p>
              <p><strong>Email:</strong> {teamData.email}</p>
              <p><strong>Contact:</strong> {teamData.contactNumber}</p>
              <p><strong>Institute:</strong> {teamData.instituteName}</p>
              <p><strong>Project:</strong> {teamData.projectTitle}</p>
              <p><strong>Theme:</strong> {teamData.theme}</p>

              {/* Leader Attendance */}
              <div className="mt-4">
                <h4 className="font-bold text-lg">Leader Attendance:</h4>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={leaderAttendance}
                    onChange={() => setLeaderAttendance((prev) => !prev)}
                  />
                  <label>{teamData.teamLeaderName}</label>
                </div>
              </div>

              {/* Members Attendance */}
              <div className="mt-4">
                <h4 className="font-bold text-lg">Team Members Attendance:</h4>
                {teamData.teamMembers.map((member) => (
                  <div key={member.name} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={memberAttendance[member.name]}
                      onChange={() => toggleMemberAttendance(member.name)}
                    />
                    <label>{member.name}</label>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 mt-4">
                <button onClick={markAttendance} className="bg-green-500 text-white px-4 py-2 rounded">
                  Update Attendance
                </button>
                <button onClick={() => { setScanResult(null); setTeamData(null); }} className="bg-gray-500 text-white px-4 py-2 rounded">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p className="text-gray-600">Fetching team details...</p>
          )}
        </div>
      ) : (
        <div id="qr-reader" className="w-full max-w-md"></div>
      )}

      {error && <p className="text-red-500 mt-2">{error}</p>}
    </div>
  );
};

export default VolunteerPage;
